// services/reportService.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Configurar locale en español
dayjs.locale('es');

/**
 * Genera reporte de Servicio de Cocina
 */
export const generateServicioCocinaReport = (data, params) => {
  return new Promise((resolve, reject) => {
    try {
      const { fechaDesde, fechaHasta, hotel } = params;
      
      // Crear documento PDF
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // ========== ENCABEZADO ==========
      // Título principal
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('SERVICIO DE COCINA', 14, 20);
      
      // Subtítulo
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${hotel || 'Hotel'} | ${fechaDesde} al ${fechaHasta}`, 14, 30);
      
      // Fecha de generación
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generado: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, 14, 37);
      
      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(14, 42, 290, 42);
      
      // ========== PREPARAR DATOS ==========
      const headers = [
        'Fecha', 'Desayuno', 'Picnic D', 'Total D', 
        'Almuerzo', 'Picnic A', 'Total A', 
        'Cena', 'Picnic C', 'Total C', 
        'Todo Incluido', 'Observaciones'
      ];

      const rows = data.map(item => [
        item.Fecha ? dayjs(item.Fecha).format('DD/MM/YYYY') : '-',
        (item.Desayuno || 0),
        (item.PicnicD || 0),
        (item.TotalD || 0),
        (item.Almuerzo || 0),
        (item.PicnicA || 0),
        (item.TotalA || 0),
        (item.Cena || 0),
        (item.PicnicC || 0),
        (item.TotalC || 0),
        (item.TodoIncluido || 0),
        (item.Observaciones || '-').substring(0, 40)
      ]);

      // Calcular y añadir fila de totales
      const totales = calculateTotales(data);
      rows.push([
        'TOTALES',
        totales.Desayuno,
        totales.PicnicD,
        totales.TotalD,
        totales.Almuerzo,
        totales.PicnicA,
        totales.TotalA,
        totales.Cena,
        totales.PicnicC,
        totales.TotalC,
        totales.TodoIncluido,
        ''
      ]);

      // ========== GENERAR TABLA ==========
      autoTable(doc, {
        startY: 48,
        head: [headers],
        body: rows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: 'center',
          valign: 'middle',
          lineColor: [180, 180, 180],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [24, 144, 255],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 28 }, // Fecha
          1: { cellWidth: 20 }, // Desayuno
          2: { cellWidth: 20 }, // Picnic D
          3: { cellWidth: 20 }, // Total D
          4: { cellWidth: 20 }, // Almuerzo
          5: { cellWidth: 20 }, // Picnic A
          6: { cellWidth: 20 }, // Total A
          7: { cellWidth: 20 }, // Cena
          8: { cellWidth: 20 }, // Picnic C
          9: { cellWidth: 20 }, // Total C
          10: { cellWidth: 28 }, // Todo Incluido
          11: { cellWidth: 45, halign: 'left' } // Observaciones
        },
        margin: { left: 14, right: 14 },
        pageBreak: 'auto',
        
        // Callback para añadir número de página
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(128);
          doc.text(
            `Página ${data.pageNumber} de ${pageCount}`,
            doc.internal.pageSize.width - 20,
            doc.internal.pageSize.height - 10
          );
        },
        
        // Resaltar fila de totales
        didParseCell: (data) => {
          if (data.row.raw && data.row.raw[0] === 'TOTALES') {
            data.cell.styles.fillColor = [230, 244, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      // Guardar PDF
      const fileName = `servicio_cocina_${fechaDesde}_${fechaHasta}.pdf`;
      doc.save(fileName);
      
      resolve(true);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      reject(error);
    }
  });
};

/**
 * Genera reporte de Servicio de Cocina por Nacionalidad
 */
export const generateServicioCocinaNacionalidadReport = (data, params) => {
  return new Promise((resolve, reject) => {
    try {
      const { fechaDesde, fechaHasta, nacionalidad, hotel } = params;
      
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Encabezado
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('SERVICIO DE COCINA POR NACIONALIDAD', 14, 20);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      let subtitle = `${hotel || 'Hotel'} | ${fechaDesde} al ${fechaHasta}`;
      if (nacionalidad) {
        subtitle += ` | Filtro: ${nacionalidad}`;
      }
      doc.text(subtitle, 14, 30);
      
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generado: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, 14, 37);
      
      doc.setDrawColor(200, 200, 200);
      doc.line(14, 42, 290, 42);

      // Columnas
      const headers = [
        'Fecha', 'Nacionalidad', 'Desayuno', 'Almuerzo', 'Cena',
        'Picnic D', 'Picnic A', 'Picnic C', 'Todo Incluido'
      ];

      const rows = data.map(item => [
        item.Fecha ? dayjs(item.Fecha).format('DD/MM/YYYY') : '-',
        item.Nacionalidad || '-',
        (item.Desayuno || 0),
        (item.Almuerzo || 0),
        (item.Cena || 0),
        (item.PicnicD || 0),
        (item.PicnicA || 0),
        (item.PicnicC || 0),
        (item.TodoIncluido || 0)
      ]);

      // Totales
      const totales = calculateTotalesNacionalidad(data);
      rows.push([
        'TOTALES',
        '',
        totales.Desayuno,
        totales.Almuerzo,
        totales.Cena,
        totales.PicnicD,
        totales.PicnicA,
        totales.PicnicC,
        totales.TodoIncluido
      ]);

      autoTable(doc, {
        startY: 48,
        head: [headers],
        body: rows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: 'center',
          valign: 'middle'
        },
        headStyles: {
          fillColor: [24, 144, 255],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 50, halign: 'left' },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
          6: { cellWidth: 22 },
          7: { cellWidth: 22 },
          8: { cellWidth: 28 }
        },
        margin: { left: 14, right: 14 },
        pageBreak: 'auto',
        
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(128);
          doc.text(
            `Página ${data.pageNumber} de ${pageCount}`,
            doc.internal.pageSize.width - 20,
            doc.internal.pageSize.height - 10
          );
        },
        
        didParseCell: (data) => {
          if (data.row.raw && data.row.raw[0] === 'TOTALES') {
            data.cell.styles.fillColor = [230, 244, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      const fileName = `servicio_cocina_nacionalidad_${fechaDesde}_${fechaHasta}.pdf`;
      doc.save(fileName);
      
      resolve(true);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      reject(error);
    }
  });
};

// Función para calcular totales
const calculateTotales = (data) => {
  return {
    Desayuno: data.reduce((sum, item) => sum + (item.Desayuno || 0), 0),
    PicnicD: data.reduce((sum, item) => sum + (item.PicnicD || 0), 0),
    TotalD: data.reduce((sum, item) => sum + (item.TotalD || 0), 0),
    Almuerzo: data.reduce((sum, item) => sum + (item.Almuerzo || 0), 0),
    PicnicA: data.reduce((sum, item) => sum + (item.PicnicA || 0), 0),
    TotalA: data.reduce((sum, item) => sum + (item.TotalA || 0), 0),
    Cena: data.reduce((sum, item) => sum + (item.Cena || 0), 0),
    PicnicC: data.reduce((sum, item) => sum + (item.PicnicC || 0), 0),
    TotalC: data.reduce((sum, item) => sum + (item.TotalC || 0), 0),
    TodoIncluido: data.reduce((sum, item) => sum + (item.TodoIncluido || 0), 0)
  };
};

const calculateTotalesNacionalidad = (data) => {
  return {
    Desayuno: data.reduce((sum, item) => sum + (item.Desayuno || 0), 0),
    Almuerzo: data.reduce((sum, item) => sum + (item.Almuerzo || 0), 0),
    Cena: data.reduce((sum, item) => sum + (item.Cena || 0), 0),
    PicnicD: data.reduce((sum, item) => sum + (item.PicnicD || 0), 0),
    PicnicA: data.reduce((sum, item) => sum + (item.PicnicA || 0), 0),
    PicnicC: data.reduce((sum, item) => sum + (item.PicnicC || 0), 0),
    TodoIncluido: data.reduce((sum, item) => sum + (item.TodoIncluido || 0), 0)
  };
};